/*!
 * DIREVULF — Fandom Hub pictures (assets/fandom-hub-pictures.js)
 *
 * No block without a picture. The hub (fandom-hub.js) shows only licence-checked Wikimedia
 * Commons images and, where none exists, a designed text card. On the owner's decision
 * (12 Oct 2026) every such text card is now given a real picture of what it is about, pulled
 * live from the item's own Wikipedia article (How to Train Your Dragon: its Fandom wiki page):
 *
 *   1. the article's lead picture;
 *   2. if that picture is already shown in the same view, another picture from the same article;
 *   3. otherwise the lead picture of a character the item involves ("Pictured: …"), then another
 *      picture from that character's article;
 *   4. then the right subject's picture again, even if it is already shown in the view;
 *   5. only a block whose subject has no usable picture at all borrows another character.
 *
 * A news headline is matched to the article it is about by its own words ("Magik VS Dormammu"
 * -> Dormammu), and that article is named under the picture.
 *
 * The text card stays only if nothing at all can be loaded. Pictures are hot-linked from
 * Wikimedia / Fandom (not re-hosted) and credited with the file's own author, licence line and
 * file page. Many of them are official studio or publisher art that Wikipedia hosts under fair
 * use: showing them on the store was the owner's choice, made knowing they are not licensed to
 * DIREVULF.
 *
 * Self-contained: reads the hub's own config and content files, never changes fandom-hub.js,
 * and works off a MutationObserver, so page sections, live headlines, panels, articles and cards
 * whose Commons picture failed to load are all covered. Lookups are cached in this browser
 * (pages 7 days, files 14 days) and sent two at a time.
 */
(function () {
  'use strict';
  var root = document.querySelector('[data-dvh]');
  if (!root || root.__dvhPics || !window.MutationObserver || !window.fetch) return;
  root.__dvhPics = true;

  var CFG = {};
  try { CFG = JSON.parse(root.querySelector('[data-dvh-config]').textContent); } catch (e) { return; }
  var DAY = 864e5, UA = { 'Api-User-Agent': 'DIREVULF-FandomHub/1.0 (https://direvulf.com)' };
  var WP_API = 'https://en.wikipedia.org/w/api.php';
  // Article furniture, not pictures of the subject: icons, logos, flags, maps, actor photos.
  var SKIP = /(\.svg$|\.gif$|logo|icon|symbol|flag of|signature|wikiquote|wiktionary|commons-logo|question book|ambox|edit-|crystal|nuvola|padlock|wikipetan|disambig|stub|portal|banner|by gage skidmore|luigi ?novi|premiere|comic-con|comic con|sdcc|\(cropped\))/i;
  // A lead picture that is a logo or wordmark is passed over too (drivers' cropped photos stay).
  var LEAD_SKIP = /(\.svg$|logo|icon|symbol|flag of|signature|wordmark)/i;
  var PERSON = ['character', 'driver', 'dragon', 'team'];
  var layer = root.querySelector('[data-dvh-layer]'), body = root.querySelector('[data-dvh-body]');

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function norm(s) { return String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim(); }
  function fileKey(f) { return String(f || '').toLowerCase().replace(/[\s_]+/g, '_'); }
  function text(html) { try { return (new DOMParser().parseFromString(String(html || ''), 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 90); } catch (e) { return ''; } }

  var store = {
    get: function (k) { try { var r = JSON.parse(localStorage.getItem('dvh:' + k)); return r && r.t ? r : null; } catch (e) { return null; } },
    set: function (k, v) { try { localStorage.setItem('dvh:' + k, JSON.stringify({ t: Date.now(), v: v })); } catch (e) {} }
  };
  function getJSON(url, headers) {
    var ctrl = window.AbortController ? new AbortController() : null, timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, 12000);
    return fetch(url, { signal: ctrl ? ctrl.signal : undefined, headers: headers || {}, credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .finally(function () { clearTimeout(timer); });
  }
  var queue = [], active = 0;
  function queued(fn) {
    return new Promise(function (res, rej) {
      queue.push(function () { active++; fn().then(res, rej).finally(function () { active--; setTimeout(pump, 120); }); });
      pump();
    });
  }
  function pump() { while (active < 2 && queue.length) queue.shift()(); }

  /* ---------------------------------------------------------------- content (the hub's own files) */
  var CORE = null, PARTS = {};
  function core() { if (!CORE) CORE = getJSON(CFG.content).catch(function () { return {}; }); return CORE; }
  function part(k) {
    if (!PARTS[k]) PARTS[k] = (CFG.parts && CFG.parts[k] ? getJSON(CFG.parts[k]) : Promise.reject(new Error('none'))).then(function (d) {
      d.byId = {};
      (d.items || []).forEach(function (i) { d.byId[i.id] = i; });
      (d.blogs || []).forEach(function (b) { d.byId[b.id] = b; });
      return d;
    });
    return PARTS[k];
  }

  /* ---------------------------------------------------------------- lookups */
  var INFO = {}, FILE = {};
  function src(code) {
    var p = String(code || '').split(':');
    if (p[0] === 'wp' && p.length > 1) return { api: 'wp', wiki: 'en', title: p.slice(1).join(':').replace(/_/g, ' ') };
    if (p[0] === 'fd' && p.length > 2) return { api: 'fd', wiki: p[1], title: p.slice(2).join(':').replace(/_/g, ' ') };
    return null;
  }
  function api(s) { return s.api === 'fd' ? 'https://' + s.wiki + '.fandom.com/api.php' : WP_API; }
  function head(s) { return s.api === 'wp' ? UA : {}; }
  function pageKey(s) { return s.api + ':' + s.wiki + ':' + (s.q ? '?' + s.q : s.title); }
  function fKey(s, f) { return s.api + ':' + s.wiki + ':' + f; }

  function infoOf(p) {
    var v = { lead: null, files: [] };
    if (!p || 'missing' in p) return v;
    v.t = p.title || '';
    if (p.pageimage && !LEAD_SKIP.test(p.pageimage)) v.lead = p.pageimage.replace(/_/g, ' ');
    v.files = (p.images || []).map(function (x) { return x.title.replace(/^[^:]+:/, ''); }).filter(function (f) { return f !== v.lead && !SKIP.test(f); });
    return v;
  }
  /* The lead picture and the other pictures of each page. */
  function pages(list) {
    var need = {};
    list.forEach(function (s) {
      var k = pageKey(s); if (INFO[k]) return;
      var c = store.get('wpi:' + k); if (c && Date.now() - c.t < 7 * DAY) { INFO[k] = c.v; return; }
      var g = s.api + ':' + s.wiki; need[g] = need[g] || { s: s, titles: [], qs: [] };
      if (s.q) { if (!need[g].qs.some(function (x) { return x.q === s.q; })) need[g].qs.push(s); } else if (need[g].titles.indexOf(s.title) < 0) need[g].titles.push(s.title);
    });
    var jobs = [];
    Object.keys(need).forEach(function (g) {
      var n = need[g];
      for (var i = 0; i < n.titles.length; i += 20) jobs.push(pageBatch(n.s, n.titles.slice(i, i + 20)));
      n.qs.forEach(function (x) { jobs.push(search(x)); });
    });
    return Promise.all(jobs.map(function (p) { return p.catch(function () {}); }));
  }
  function pageBatch(s, titles) {
    var url = api(s) + '?action=query&format=json&origin=*&redirects=1&prop=pageimages|images&piprop=name&pilicense=any&pilimit=50&imlimit=max&titles=' + encodeURIComponent(titles.join('|'));
    return queued(function () { return getJSON(url, head(s)); }).then(function (j) {
      var q = j.query || {}, map = {}, byTitle = {};
      titles.forEach(function (t) { map[t] = t; });
      (q.normalized || []).concat(q.redirects || []).forEach(function (n) { titles.forEach(function (t) { if (map[t] === n.from) map[t] = n.to; }); });
      Object.keys(q.pages || {}).forEach(function (id) { byTitle[q.pages[id].title] = q.pages[id]; });
      titles.forEach(function (t) { var k = pageKey({ api: s.api, wiki: s.wiki, title: t }), v = infoOf(byTitle[map[t]]); INFO[k] = v; store.set('wpi:' + k, v); });
    });
  }
  /* A slot with no page of its own in the content is searched for: a label by its name (first
     result), a headline by its own words, taking the first result with a picture whose name the
     headline actually contains ("Magik VS Dormammu" finds Dormammu; "List of Marvel Comics
     characters" or an unrelated hit is passed over), first with the fandom's name added, then
     without; failing that, the article named by two or three of its words in a row ("Darth Vader
     Gains The Zaly Shield" -> Darth Vader). A headline with no such article keeps to the names
     in its kicker. */
  function fits(title, need) {
    var b = norm(String(title || '').replace(/\s*\([^)]*\)\s*$/, ''));
    return b.length > 2 && (' ' + need + ' ').indexOf(' ' + b + ' ') > -1;
  }
  function search(s) {
    var tries = [s.q].concat(s.alt ? [s.alt] : []);
    function attempt(i) {
      if (i >= tries.length) return null;
      var url = api(s) + '?action=query&format=json&origin=*&redirects=1&generator=search&gsrlimit=' + (s.need ? 5 : 1) + '&gsrsearch=' + encodeURIComponent(tries[i]) + '&prop=pageimages|images&piprop=name&pilicense=any&imlimit=max';
      return queued(function () { return getJSON(url, head(s)); }).then(function (j) {
        var ps = (j.query && j.query.pages) || {};
        var list = Object.keys(ps).map(function (k) { return ps[k]; }).sort(function (a, b) { return (a.index || 0) - (b.index || 0); });
        var hit = s.need ? list.filter(function (p) { return p.pageimage && fits(p.title, s.need); })[0] : list[0];
        return hit || attempt(i + 1);
      });
    }
    return attempt(0).then(function (p) { return p || (s.need ? phrase(s) : null); })
      .then(function (p) { var k = pageKey(s), v = infoOf(p); INFO[k] = v; store.set('wpi:' + k, v); });
  }
  var JOIN = ['vs', 'v', 'the', 'a', 'an', 'of', 'on', 'in', 'at', 'as', 'to', 'and', 'or', 'how', 'why', 'what', 'who', 'when', 'with', 'for', 'from', 'by', 'is', 'are', 'was', 'his', 'her', 'their', 'into', 'after', 'before', 'new'];
  function phrase(s) {
    var words = String(s.head || '').split(/\s+/).map(function (x) { return x.replace(/^[^\w]+|[^\w]+$/g, ''); }), cand = [];
    for (var i = 0; i < words.length; i++) {
      [3, 2].forEach(function (n) {
        var w = words.slice(i, i + n);
        if (w.length === n && !w.some(function (x) { return JOIN.indexOf(x.toLowerCase()) > -1 || x.length < 2; })) cand.push(w.join(' '));
      });
    }
    if (!cand.length) return null;
    var url = api(s) + '?action=query&format=json&origin=*&redirects=1&prop=pageimages|images|pageprops&ppprop=disambiguation&piprop=name&pilicense=any&imlimit=max&titles=' + encodeURIComponent(cand.slice(0, 40).join('|'));
    return queued(function () { return getJSON(url, head(s)); }).then(function (j) {
      var q = j.query || {}, to = {}, byTitle = {};
      (q.normalized || []).concat(q.redirects || []).forEach(function (n) { to[n.from] = n.to; });
      Object.keys(q.pages || {}).forEach(function (id) { byTitle[q.pages[id].title] = q.pages[id]; });
      for (var i = 0; i < cand.length; i++) {
        var t = cand[i], hops = 0; while (to[t] && hops++ < 4) t = to[t];
        var p = byTitle[t];
        if (p && !('missing' in p) && !('invalid' in p) && p.pageimage && !(p.pageprops && 'disambiguation' in p.pageprops)) return p;
      }
      return null;
    });
  }
  /* A 640px copy of each file, its size, author and licence line. Icons and thumbnails are
     ruled out here: a picture needs 150px on its short side and 50,000 pixels in all (a tall
     174 x 576 character portrait passes, a 120px badge does not). */
  function files(reqs) {
    var need = {};
    reqs.forEach(function (r) {
      var k = fKey(r.s, r.f); if (k in FILE) return;
      var c = store.get('wpf:' + k); if (c && Date.now() - c.t < 14 * DAY) { FILE[k] = c.v; return; }
      var g = r.s.api + ':' + r.s.wiki; need[g] = need[g] || { s: r.s, files: [] };
      if (need[g].files.indexOf(r.f) < 0) need[g].files.push(r.f);
    });
    var jobs = [];
    Object.keys(need).forEach(function (g) {
      var n = need[g];
      for (var i = 0; i < n.files.length; i += 40) (function (chunk) {
        var url = api(n.s) + '?action=query&format=json&origin=*&prop=imageinfo&iiprop=url|size|mime|extmetadata&iiextmetadatafilter=LicenseShortName|Artist&iiurlwidth=640&titles=' + encodeURIComponent(chunk.map(function (f) { return 'File:' + f; }).join('|'));
        jobs.push(queued(function () { return getJSON(url, head(n.s)); }).then(function (j) {
          var q = j.query || {}, back = {};
          (q.normalized || []).forEach(function (x) { back[x.to] = x.from; });
          Object.keys(q.pages || {}).forEach(function (id) {
            var p = q.pages[id], ii = p.imageinfo && p.imageinfo[0], name = (back[p.title] || p.title).replace(/^[^:]+:/, ''), v = null;
            if (ii && /^image\//.test(ii.mime || '') && !/svg|gif/.test(ii.mime || '') && Math.min(ii.width || 0, ii.height || 0) >= 150 && (ii.width || 0) * (ii.height || 0) >= 50000) {
              var em = ii.extmetadata || {};
              v = { u: ii.thumburl || ii.url, o: ii.url, w: ii.thumbwidth || ii.width, h: ii.thumbheight || ii.height,
                lic: text((em.LicenseShortName || {}).value), by: text((em.Artist || {}).value), page: ii.descriptionurl || '' };
            }
            var k = fKey(n.s, name); FILE[k] = v; store.set('wpf:' + k, v);
          });
          chunk.forEach(function (f) { var k = fKey(n.s, f); if (!(k in FILE)) FILE[k] = null; });
        }).catch(function () {}));
      })(n.files.slice(i, i + 40));
    });
    return Promise.all(jobs);
  }

  /* ---------------------------------------------------------------- what is already shown */
  function viewOf(el) { return layer && layer.contains(el) ? layer : body; }
  /* The file a picture's address points at. Two file names can be one picture (a redirect such
     as Giantsize1.jpg -> Giant-Size X-Men (no. 1 - cover).jpg), so the address is compared too. */
  function urlKey(u) {
    var m = String(u || '').match(/\/(?:commons|en|images)\/(?:thumb\/)?[0-9a-f]\/[0-9a-f]{2}\/([^/?#]+)/i);
    if (!m) return '';
    try { return fileKey(decodeURIComponent(m[1])); } catch (e) { return fileKey(m[1]); }
  }
  function shownIn(view) {
    var used = {};
    Array.prototype.forEach.call(view.querySelectorAll('img'), function (im) {
      var cur = im.getAttribute('data-cur') || '', k = urlKey(im.getAttribute('src'));
      if (/^w:/.test(cur)) used[fileKey(cur.slice(2))] = 1;
      if (k) used[k] = 1;
    });
    PENDING.forEach(function (p) { if (p.el.isConnected && viewOf(p.el) === view) { used[fileKey(p.f)] = 1; if (p.k) used[p.k] = 1; } });
    return used;
  }
  var PENDING = [];

  /* ---------------------------------------------------------------- what a slot is about */
  function byName(d, name) { var n = norm(name); return n ? (d.items || []).filter(function (i) { return norm(i.name) === n; })[0] : null; }
  function subject(fb, d, fk, fname) {
    var c = fb.getAttribute('data-cid') || '', pre = fk + '-', id = c.indexOf(pre) === 0 ? c.slice(pre.length) : '';
    var main = null, rel = [], q = '', hq = '';
    var box = fb.closest('.dvh-card, .dvh-mini, .dvh-moment');
    if (/^cn-/.test(id)) {
      var kick = box && box.querySelector('.dvh-kicker'), ttl = box && box.querySelector('.dvh-card__title, h3, h4');
      (kick ? kick.textContent.split('·') : []).forEach(function (n) { var it = byName(d, n.trim()); if (it) rel.push(it); });
      hq = ttl ? ttl.textContent.replace(/\s+/g, ' ').trim() : '';
    } else if (/-moment$/.test(id)) {
      var mb = d.byId[id.replace(/-moment$/, '')]; if (mb && d.byId[mb.char]) rel.push(d.byId[mb.char]);
    } else if (id && d.byId[id]) {
      var o = d.byId[id];
      if (o.title && o.char) {
        if (d.byId[o.char]) main = d.byId[o.char];
        var R = o.rel || {}; ['allies', 'rivals', 'teams', 'family', 'mentors'].forEach(function (g) { (R[g] || []).forEach(function (r) { if (r.id && d.byId[r.id]) rel.push(d.byId[r.id]); }); });
      } else { main = o; (o.rel || []).forEach(function (r) { if (d.byId[r]) rel.push(d.byId[r]); }); }
    } else {
      var lab = (fb.getAttribute('aria-label') || '').split(' — ')[0].trim();
      if (fname && lab.slice(-(fname.length + 2)) === ', ' + fname) lab = lab.slice(0, -(fname.length + 2)).trim();
      var it2 = lab && byName(d, lab);
      if (it2) main = it2; else if (lab) q = lab;
    }
    var list = [], seen = {};
    function add(s, pictured) { if (!s) return; var k = pageKey(s); if (seen[k]) return; seen[k] = 1; list.push({ s: s, p: pictured || '' }); }
    // An item may name its own picture page (pic) when the text source (src) has no picture of it.
    if (main) add(src(main.pic || main.src), '');
    var fs = src((d.items || []).map(function (i) { return i.src; }).filter(Boolean)[0]) || { api: 'wp', wiki: 'en' };
    if (q) add({ api: fs.api, wiki: fs.wiki, q: q + (fs.api === 'wp' && fname ? ' ' + fname : '') }, '');
    if (hq) add({ api: fs.api, wiki: fs.wiki, q: hq + (fs.api === 'wp' && fname ? ' ' + fname : ''), alt: fs.api === 'wp' && fname ? hq : '', need: norm(hq), head: hq }, '');
    var own = list.length;
    rel.forEach(function (r) { add(src(r.src), r.name); });
    var nrel = list.length;
    (d.items || []).filter(function (i) { return PERSON.indexOf(i.type) > -1; }).forEach(function (i) { if (i !== main) add(src(i.src), i.name); });
    return { pages: list, own: own, rel: nrel, name: main ? main.name : q };
  }

  /* ---------------------------------------------------------------- choosing */
  // A slot's pages come in three groups: its own article, the articles of what it names as
  // related (a story's characters, a headline's kicker names), and the fandom's other characters.
  //   stage 1 own lead · 2 more from its own article · 3 a related article's lead · 4 more from a
  //   related article · 5 its own or a related lead even if already shown · 6 another character's
  //   lead · 7 any lead already shown
  // A picture of the right subject shown twice is better than a picture of the wrong one, so a
  // slot with its own or related pages repeats one of them (stage 5) before it borrows another
  // character (stage 6). Otherwise nothing repeats in a view. An article's other pictures count
  // only when the file is named after the page or the subject (a navbox stamp or an unrelated
  // photo in the same article is passed over); "X-Men" also matches "xmen".
  var STOP = ['the', 'and', 'comics', 'comic', 'series', 'film', 'films', 'character', 'franchise', 'marvel', 'with', 'from', 'story', 'tv', 'your'];
  function tokens(a) {
    var t = [];
    a.forEach(function (x) {
      var w = norm(x).split(' ').filter(function (v) { return v && STOP.indexOf(v) < 0; }), whole = w.join('');
      w.concat(whole).forEach(function (v) { if (v.length > 3 && t.indexOf(v) < 0) t.push(v); });
    });
    return t;
  }
  function named(f, toks) { var n = norm(f), c = n.replace(/ /g, ''); return toks.some(function (t) { return n.indexOf(t) > -1 || c.indexOf(t) > -1; }); }
  function group(job, i) { return i < job.subj.own ? 'own' : i < job.subj.rel ? 'rel' : 'fill'; }
  function pick(job, stage, used) {
    var P = job.subj.pages, want = { 1: 'own', 2: 'own', 3: 'rel', 4: 'rel', 5: 'own rel', 6: 'fill' }[stage];
    var more = stage === 2 || stage === 4, again = stage === 5 || stage === 7;
    for (var i = 0; i < P.length; i++) {
      var pg = P[i], info = INFO[pageKey(pg.s)], g = group(job, i);
      if (!info || (want && want.indexOf(g) < 0)) continue;
      var toks = tokens([pg.s.title || pg.s.q || '', g === 'own' ? job.subj.name : pg.p]);
      var list = more ? info.files.filter(function (f) { return named(f, toks); }) : [info.lead].filter(function (f) { return f && !LEAD_SKIP.test(f); });
      for (var j = 0; j < list.length; j++) {
        var f = list[j], fi = f && FILE[fKey(pg.s, f)];
        if (fi && (again || !(used[fileKey(f)] || used[urlKey(fi.u)]))) return { pg: pg, f: f, fi: fi, own: g === 'own', t: pg.s.need ? info.t : '' };
      }
    }
    return null;
  }
  function leadReqs(jobs) {
    var out = [];
    jobs.forEach(function (j) { j.subj.pages.forEach(function (pg) { var info = INFO[pageKey(pg.s)]; if (info && info.lead) out.push({ s: pg.s, f: info.lead }); }); });
    return out;
  }
  function moreReqs(jobs, which) {
    var out = [];
    jobs.forEach(function (j) {
      j.subj.pages.forEach(function (pg, i) {
        var g = group(j, i); if (g !== which) return;
        var info = INFO[pageKey(pg.s)], toks = tokens([pg.s.title || pg.s.q || '', g === 'own' ? j.subj.name : pg.p]);
        if (info) info.files.filter(function (f) { return named(f, toks); }).slice(0, 8).forEach(function (f) { out.push({ s: pg.s, f: f }); });
      });
    });
    return out;
  }
  function fill(fbs) {
    var fk = body && body.getAttribute('data-fandom');
    if (!fk) { fbs.forEach(function (x) { x.__dvw = 0; }); return; }
    Promise.all([core(), part(fk)]).then(function (r) {
      var fc = ((r[0] && r[0].fandoms) || []).filter(function (x) { return x.key === fk; })[0] || {}, d = r[1];
      var jobs = fbs.filter(function (fb) { return fb.isConnected; }).map(function (fb) { return { fb: fb, view: viewOf(fb), subj: subject(fb, d, fk, fc.name || ''), got: null }; }).filter(function (j) { return j.subj.pages.length; });
      if (!jobs.length) return;
      var all = []; jobs.forEach(function (j) { j.subj.pages.forEach(function (pg) { all.push(pg.s); }); });
      function stage(n) {
        jobs.forEach(function (j) {
          if (j.got || !j.fb.isConnected) return;
          j.got = pick(j, n, shownIn(j.view));
          if (j.got) PENDING.push({ el: j.fb, f: j.got.f, k: urlKey(j.got.fi.u) });
        });
      }
      function open() { return jobs.filter(function (j) { return !j.got && j.fb.isConnected; }); }
      return pages(all)
        .then(function () { return files(leadReqs(jobs)); })
        .then(function () { stage(1); var o = open(); return o.length ? files(moreReqs(o, 'own')) : null; })
        .then(function () { stage(2); stage(3); var o = open(); return o.length ? files(moreReqs(o, 'rel')) : null; })
        .then(function () { stage(4); stage(5); stage(6); stage(7); jobs.forEach(function (j) { if (j.got && j.fb.isConnected) paint(j); }); });
    }).catch(function () {});
  }

  /* ---------------------------------------------------------------- painting */
  function credit(g, pictured) {
    var fi = g.fi, via = g.pg.s.api === 'fd' ? 'Fandom' : 'Wikipedia';
    return (pictured ? 'Pictured: ' + esc(pictured) + ' · ' : '') + 'Image: <a href="' + esc(fi.page || '#') + '" target="_blank" rel="noopener">' + esc(fi.by || g.f) + '</a>' +
      (fi.lic ? ' · ' + esc(fi.lic) : '') + ' · via ' + via;
  }
  function creditsList(key, html) {
    var host = layer && layer.querySelector('.dvh-panel__body, .dvh-art__body'); if (!host) return;
    var sec = host.querySelector('[data-dvh-imgcredits]');
    if (!sec) { host.insertAdjacentHTML('beforeend', '<section class="dvh-block dvh-imgcredits" data-dvh-imgcredits><h3 class="dvh-h3">Image credits</h3><ul></ul></section>'); sec = host.querySelector('[data-dvh-imgcredits]'); }
    var ul = sec.querySelector('ul'), dup = false;
    Array.prototype.forEach.call(ul.children, function (li) { if (li.getAttribute('data-key') === key) dup = true; });
    if (dup) return;
    var li = document.createElement('li'); li.setAttribute('data-key', key); li.innerHTML = html; ul.appendChild(li);
  }
  var SAVED = typeof WeakMap === 'function' ? new WeakMap() : null;
  function paint(j) {
    // A headline's picture comes from the article it was matched to: that article is named.
    var fb = j.fb, g = j.got, fi = g.fi, pictured = g.own ? String(g.t || '').replace(/\s*\([^)]*\)\s*$/, '') : g.pg.p;
    var img = document.createElement('img');
    img.className = 'dvh-img';
    // Fandom (static.wikia.nocookie.net) answers hot-linked requests that carry another site's
    // Referer with a 300px "image not available" placeholder; without a Referer it sends the file.
    img.referrerPolicy = 'no-referrer';
    img.src = fi.u; img.width = 640; img.height = Math.max(1, Math.round(640 * (fi.h || 480) / (fi.w || 640)));
    img.alt = pictured || j.subj.name || ''; img.loading = 'lazy'; img.decoding = 'async';
    img.setAttribute('data-dvh-wimg', ''); img.setAttribute('data-cur', 'w:' + g.f); img.setAttribute('data-tier', '4');
    ['data-cid', 'data-sec', 'data-pool'].forEach(function (a) { if (fb.hasAttribute(a)) img.setAttribute(a, fb.getAttribute(a)); });
    if (!fb.hasAttribute('data-cid')) img.setAttribute('data-nav', '');
    if (fi.o && fi.o !== fi.u) img.setAttribute('data-orig', fi.o);
    if (SAVED) SAVED.set(img, fb);
    var inLayer = layer && layer.contains(fb), html = credit(g, pictured), key = 'w:' + g.f;
    fb.parentNode.replaceChild(img, fb);
    PENDING = PENDING.filter(function (p) { return p.el !== fb && p.el.isConnected; });
    var card = img.closest('.dvh-card'), pm = img.closest('.dvh-panel__media'), hero = img.closest('.dvh-article__hero'), mom = img.closest('.dvh-moment');
    var p = '<p class="dvh-credit" data-dvh-credit>' + html + '</p>';
    if (card && !card.querySelector('[data-dvh-credit]')) card.insertAdjacentHTML('beforeend', p);
    else if (pm && !pm.querySelector('[data-dvh-credit]')) pm.insertAdjacentHTML('beforeend', p);
    else if (hero && !(hero.nextElementSibling && hero.nextElementSibling.classList.contains('dvh-art__herocap'))) hero.insertAdjacentHTML('afterend', '<p class="dvh-credit dvh-art__herocap" data-dvh-credit>' + html + '</p>');
    else if (mom && !mom.querySelector('[data-dvh-credit]') && mom.children[1]) mom.children[1].insertAdjacentHTML('beforeend', '<p class="dvh-credit dvh-credit--inline" data-dvh-credit>' + html + '</p>');
    if (inLayer) creditsList(key, html);
  }
  /* A picture that will not load tries its full-size file once, then hands the slot back to the
     text card. */
  root.addEventListener('error', function (e) {
    var t = e.target;
    if (!t || t.tagName !== 'IMG' || !t.hasAttribute('data-dvh-wimg')) return;
    var o = t.getAttribute('data-orig');
    if (o) { t.removeAttribute('data-orig'); t.src = o; return; }
    var fb = SAVED && SAVED.get(t);
    if (fb && t.parentNode) { fb.__dvw = 1; t.parentNode.replaceChild(fb, t); }
  }, true);

  /* ---------------------------------------------------------------- watching the hub */
  var timer = null;
  function scan() {
    timer = null;
    // Not product tiles (those wait for the product's own photo) and not the licensed F1 news
    // feed's cards (rendered without dvh-reveal), whose pictures come only with that licence.
    var list = Array.prototype.filter.call(root.querySelectorAll('.dvh-fb'), function (x) {
      return !x.__dvw && x.isConnected && !x.closest('.dvh-prod__media, .dvh-card--news:not(.dvh-reveal)');
    });
    list.forEach(function (x) { x.__dvw = 1; });
    if (list.length) fill(list);
  }
  new MutationObserver(function () { if (!timer) timer = setTimeout(scan, 80); }).observe(root, { childList: true, subtree: true });
  scan();
})();
